/**
 * IssuePaymentNoticeModal Component
 * Creates a payment notice against a specific milestone — prefilled from the
 * cumulative shortfall. Only one active (pending) notice per milestone is allowed.
 */

import React, { useState, useEffect } from 'react';
import { formatCurrency } from '../../utils/formatters';
import { paymentNoticeAPI } from '../../services/api';
import { toast } from 'react-toastify';

const IssuePaymentNoticeModal = ({ isOpen, onClose, onSuccess, milestone, projectId }) => {
  const today = new Date().toISOString().slice(0, 10);

  const prefillAmount = () => {
    if (!milestone) return '';
    const due = parseFloat(milestone.paymentDue || 0);
    return due > 0 ? due.toFixed(2) : '';
  };

  const [amount, setAmount] = useState(prefillAmount());
  const [issuedAt, setIssuedAt] = useState(today);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setAmount(prefillAmount());
      setIssuedAt(today);
      setNotes('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, milestone?.id]);

  if (!isOpen || !milestone) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const parsed = parseFloat(amount);
    if (!parsed || parsed <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setSubmitting(true);
    try {
      await paymentNoticeAPI.issue(projectId, milestone.id, {
        amount: parsed,
        issuedAt,
        notes
      });
      toast.success('Payment notice issued');
      onSuccess();
    } catch (err) {
      console.error('Error issuing notice:', err);
      toast.error(err.response?.data?.error || 'Failed to issue notice');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-gray-500 bg-opacity-75 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0" onClick={onClose} />
      <div className="relative bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full sm:max-w-lg sm:mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <form onSubmit={handleSubmit} className="flex flex-col max-h-[90vh]">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex-shrink-0">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              Issue Payment Notice
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              {milestone.milestoneName} · outstanding {formatCurrency(milestone.paymentDue || 0)}
            </p>
          </div>

          <div className="px-4 sm:px-6 py-4 overflow-y-auto space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notice Amount (RM) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
              <p className="text-xs text-gray-400 mt-1">
                Prefilled from the milestone's cumulative shortfall. Override if you're requesting a different amount.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Issued On <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={issuedAt}
                onChange={e => setIssuedAt(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                rows={3}
                value={notes}
                onChange={e => setNotes(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="Optional context (e.g. notice sent via email, follow-up scheduled)…"
              />
            </div>
          </div>

          <div className="px-4 sm:px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row gap-2 sm:justify-end flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
            >
              {submitting ? 'Issuing…' : 'Issue Notice'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default IssuePaymentNoticeModal;
